import { Spacer } from "@hackthenorth/north";
import { Label, Link } from "north.js";
import React from "react";

import { Dropzone, FileLink } from "src/shared/components";
import { isValidUrl } from "src/shared/utils/validation";
import { NewsletterFeatureData } from "src/views/sponsor/perks/sync";
import { PerkStatus, SponsorPerkType } from "src/views/sponsor/perks/types";

import {
  PerkContainer,
  PerkError,
  PerkTextArea,
  PerkTextInput,
} from "../components";
import Header from "../Header";
import { getPerkStatusLabel, perkStatusToBadgeColor } from "../helpers";
import { TextDescription } from "../TextComponents";
import { useFileDropzone } from "../useFileDropzone";
import { PerkData } from "../usePerkState";

import Preview from "./Preview";

export const NEWSLETTER_FEATURE_VALIDATION_FIELDS: (keyof NewsletterFeatureData)[] =
  [
    "newsletter_feature_image_file",
    "newsletter_feature_title",
    "newsletter_feature_website_link",
    "newsletter_feature_description",
  ];

export const NewsletterFeature: React.FC<PerkData> = ({
  getState,
  updateState,
  isReadOnly,
  error,
}) => {
  const {
    dropzoneInputRef,
    fetchedFile,
    error: fileError,
    addFiles,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  } = useFileDropzone(
    getState("newsletter_feature_image_file") ?? [],
    (files) => updateState("newsletter_feature_image_file", files)
  );

  return (
    <>
      <Header
        title="Newsletter: Feature"
        subtitle="This perk is for features in our monthly newsletter."
        badge={
          <Label
            color={perkStatusToBadgeColor(
              getState("newsletter_feature_status") as PerkStatus | undefined
            )}
            size="sm"
          >
            {getPerkStatusLabel(
              getState("newsletter_feature_status") as PerkStatus | undefined
            )}
          </Label>
        }
      />
      <PerkContainer
        description={<Description />}
        id={SponsorPerkType.NEWSLETTER_FEATURE}
        readOnly={isReadOnly}
      >
        {!isReadOnly && (
          <Dropzone
            max={1}
            acceptedFileTypes={["png", "svg"]}
            onFilesUpload={addFiles}
            onError={onFileError}
            ref={dropzoneInputRef}
            onBlur={() => setDropzoneErrorMessage("")}
          />
        )}
        {fetchedFile && (
          <>
            <Spacer height={32} />
            <FileLink file={fetchedFile} deleteFile={deleteFile} />
          </>
        )}
        {error?.newsletter_feature_image_file && (
          <PerkError error="This field is required" />
        )}
        {fileError && <PerkError error={fileError} />}
        <Spacer height={32} />
        <PerkTextInput
          title="Title*"
          value={getState("newsletter_feature_title") ?? ""}
          placeholder="Your company's home or recruiting page"
          isReadOnly={isReadOnly}
          onChange={(e) =>
            updateState("newsletter_feature_title", e.target.value)
          }
          error={error?.newsletter_feature_title && <>This field is required</>}
        />
        <Spacer height={32} />
        <PerkTextInput
          title="Website Link*"
          value={getState("newsletter_feature_website_link") ?? ""}
          placeholder="Ex. Home page"
          isReadOnly={isReadOnly}
          onChange={(e) =>
            updateState("newsletter_feature_website_link", e.target.value)
          }
          error={
            (error?.newsletter_feature_website_link && (
              <>This field is required</>
            )) ||
            (!isValidUrl(getState("newsletter_feature_website_link")) && (
              <>Please enter a valid URL.</>
            ))
          }
        />
        <Spacer height={32} />
        <PerkTextArea
          title="Description* (max 40 words)"
          value={getState("newsletter_feature_description") ?? ""}
          placeholder="Share a message with hackers"
          wordLimit={40}
          isReadOnly={isReadOnly}
          onChange={(e) =>
            updateState("newsletter_feature_description", e.target.value)
          }
          error={
            error?.newsletter_feature_description && <>This field is required</>
          }
        />
        {fetchedFile && (
          <>
            <Spacer height={32} />
            <Preview
              image={fetchedFile}
              title={getState("newsletter_feature_title") ?? ""}
              link={getState("newsletter_feature_website_link") ?? ""}
              description={getState("newsletter_feature_description") ?? ""}
            />
          </>
        )}
      </PerkContainer>
    </>
  );
};

const Description = () => (
  <>
    <TextDescription>
      The Hack the North newsletter is sent out to hackers in the middle of
      every month.
    </TextDescription>
    <Spacer height={16} />
    <TextDescription>
      The Features will appear in the middle of the newsletter email.
    </TextDescription>
    <Spacer height={16} />
    <TextDescription>
      If your submission is selected for the upcoming month&apos;s newsletter,
      you will see this perk under the Complete section. Please allow us at
      least a week following your submission for our team to review.
    </TextDescription>
    <Spacer height={16} />
    <TextDescription>
      Check out our{" "}
      <Link
        href="https://hackthenorth.com/sponsorship-newsletter-template.pdf"
        target="_blank"
      >
        sample newsletter template here
      </Link>{" "}
      to see an example of the layout.
    </TextDescription>
    <Spacer height={16} />
    <TextDescription>
      Please submit an image in SVG or PNG form (SVG is preferred).
    </TextDescription>
  </>
);
